/* I&C · motion module "ambient": scroll-progress line, falling leaves on the bare dark sections,
   cursor candle-glow (desktop) and a gold hairline that grows from the centre as each section enters.
   Vanilla ES2018, single IIFE, no globals. Does nothing under prefers-reduced-motion.
   Leaves live in viewport space on a fixed z -1 canvas: the bare sections are windows onto a layer that sits
   behind the page, so the scroll never moves, accelerates or re-seeds a leaf (see NOTES.md, "scroll bug"). */
(function () {
  'use strict';
  if (!window.matchMedia) return;
  var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq.matches) return;

  function init() {
    var doc = document, html = doc.documentElement, body = doc.body;
    if (html.hasAttribute('data-rd-m-ambient')) return;
    var q = function (s) { return doc.querySelector(s); };
    var feat = q('.rd .feature');
    var roots = [q('.rd .statement'), q('.rd #rd-edit'), q('.rd .quiz-cta'), q('.rd .collections'),
      feat && (feat.closest('section') || feat), q('.rd #rd-promise'), q('.rd .letters')].filter(Boolean);
    if (!roots.length) return;
    html.setAttribute('data-rd-m-ambient', '');

    var fine = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1024px)').matches;
    var phone = window.innerWidth < 760 || !window.matchMedia('(pointer: fine)').matches;
    var LEAVES = phone ? 6 : 12;
    var sy = window.pageYOffset || 0, W = window.innerWidth, H = window.innerHeight, maxScroll = 1;
    var bands = [], running = false, leafVisible = 0, leafOn = false, disposed = false;
    var raf = window.requestAnimationFrame.bind(window);

    /* ---- arm sections: class + hairline span (idempotent) ---- */
    roots.forEach(function (el) {
      if (el.hasAttribute('data-rd-m-ambient')) return;
      el.setAttribute('data-rd-m-ambient', '');
      var cs = getComputedStyle(el);
      el.classList.add('rd-m-ambient');
      if (cs.position === 'static') el.classList.add('rd-m-ambient--rel');
      var hair = doc.createElement('span');
      hair.className = 'rd-m-ambient-hair';
      hair.setAttribute('aria-hidden', 'true');
      el.insertBefore(hair, el.firstChild);
      var bg = cs.backgroundColor, bare = /rgba\(0, 0, 0, 0\)|transparent/.test(bg);
      bands.push({ el: el, hair: hair, dark: !el.classList.contains('on-ivory'), leaf: bare && !el.classList.contains('on-ivory'), top: 0, bottom: 0, h: 0 });
    });
    var leafBands = bands.filter(function (b) { return b.leaf; });

    /* ---- fixed layers ---- */
    var make = function (tag, cls) {
      var e = doc.createElement(tag); e.className = 'rd ' + cls; e.setAttribute('aria-hidden', 'true'); body.appendChild(e); return e;
    };
    var bar = make('div', 'rd-m-ambient-progress');
    /* the leaves fall in front of the whole page below the hero (never catching clicks), continuing the ones that slip off the table */
    var canvas = make('canvas', 'rd-m-ambient-leaves');
    canvas.style.zIndex = '40'; canvas.style.pointerEvents = 'none';
    var heroEl = doc.querySelector('.hero--season'), heroBot = 0;
    var ctx = canvas && canvas.getContext('2d');
    var glow = fine ? make('div', 'rd-m-ambient-glow') : null;
    var dpr = 1, cw = 0, ch = 0;

    /* ---- measure (never inside the frame loop) ---- */
    function measure() {
      var oW = W, oH = H;
      sy = window.pageYOffset || 0; W = window.innerWidth; H = window.innerHeight;
      maxScroll = Math.max(1, html.scrollHeight - H);
      bands.forEach(function (b) {
        var r = b.el.getBoundingClientRect();
        b.top = r.top + sy; b.bottom = r.bottom + sy; b.h = r.height;
      });
      heroBot = heroEl ? heroEl.getBoundingClientRect().bottom + sy : 0;
      if (leaves.length === 0) seed();
      else if (W !== oW || H !== oH) leaves.forEach(function (l) { l.x *= W / oW; l.y *= H / oH; }); /* keep the flock in frame, no re-seed */
      if (canvas) {
        dpr = Math.min(window.devicePixelRatio || 1, 1.5); /* soft shapes: 1.5x is plenty, cheaper texture upload */
        var w = Math.round(W * dpr), h = Math.round(H * dpr);
        /* setting width wipes the bitmap: only on a real change (body RO fires on any height change), then repaint */
        if (w !== cw || h !== ch) {
          cw = w; ch = h; canvas.width = w; canvas.height = h;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          if (leafVisible > 0) stepLeaves(lastT, 0);
        }
      }
      progress();
    }
    var measureQueued = false;
    function queueMeasure() { if (measureQueued) return; measureQueued = true; raf(function () { measureQueued = false; measure(); }); }

    /* ---- 1 · scroll progress ---- */
    var progressQueued = false;
    function progress() {
      progressQueued = false;
      var p = Math.min(1, Math.max(0, sy / maxScroll));
      bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      bar.classList.toggle('is-on', sy > 24);
    }
    function onScroll() {
      sy = window.pageYOffset || 0;
      if (!progressQueued) { progressQueued = true; raf(progress); }
      if (leafVisible > 0) kick();
      if (glow) glowHit();
    }

    /* ---- 2 · leaves ----
       Screen-space particles. A leaf has a viewport x/y, falls at its own speed, sways with the wind and re-enters
       at the top when it leaves the bottom. The scroll position is used for one thing only: clipping the paint to
       the bare sections' current rects (cached document tops − sy), so nothing shows over the footer or any
       section that paints its own background. No parallax term, no document anchoring, no per-band bookkeeping:
       a wheel step, an anchor jump, a tab switch or a resize cannot push a leaf anywhere. */
  /* a maple leaf sprite: five pointed lobes with side points and serrated edges, warm autumn colour with soft blotches,
     tapered pale veins, a gentle shade towards the edges and a curved stem */
  function mapleLeaf(size, pal) {
    var cv = document.createElement('canvas'); cv.width = cv.height = size;
    var g = cv.getContext('2d'), cx = size / 2, cy = size * 0.6, R = size * 0.46, rnd = Math.random;
    var j = function (v, k) { return v * (1 + (rnd() - 0.5) * k); };
    var rgba = function (c, a) { return 'rgba(' + c.join(',') + ',' + a + ')'; };
    var lobes = [[0, j(0.68, 0.1), 0.62], [-1.0, j(0.6, 0.12), 0.55], [1.0, j(0.6, 0.12), 0.55], [-1.95, j(0.34, 0.15), 0.42], [1.95, j(0.34, 0.15), 0.42]];
    var subs = [];
    lobes.slice(0, 3).forEach(function (l) { subs.push([l[0] - 0.36, l[1] * 0.62, 0.16], [l[0] + 0.36, l[1] * 0.62, 0.16]); });
    var all = lobes.concat(subs), ph = rnd() * 6, pts = [], N = 300, tips = [];
    for (var i = 0; i <= N; i++) {
      var th = -2.55 + 5.1 * i / N, r = 0.36;
      for (var k = 0; k < all.length; k++) { var d = Math.abs(th - all[k][0]); if (d < all[k][2]) r = Math.max(r, 0.36 + all[k][1] * Math.pow(1 - d / all[k][2], 1.15)); }
      if (r > 0.42) r += 0.03 * Math.pow(Math.abs(Math.sin(th * 26 + ph)), 4) * r;
      pts.push([cx + Math.sin(th) * r * R, cy - Math.cos(th) * r * R]);
    }
    lobes.forEach(function (l) { tips.push([cx + Math.sin(l[0]) * (0.36 + l[1]) * R * 0.92, cy - Math.cos(l[0]) * (0.36 + l[1]) * R * 0.92]); });
    var path = function () { g.beginPath(); g.moveTo(cx, cy + R * 0.06); pts.forEach(function (p) { g.lineTo(p[0], p[1]); }); g.closePath(); };
    // stem: tapered, darker at the tip
    var sg = g.createLinearGradient(cx, cy, cx + size * 0.06, cy + R * 0.42); sg.addColorStop(0, rgba(pal[2], 1)); sg.addColorStop(1, rgba(pal[2].map(function (v) { return Math.round(v * 0.7); }), 1));
    g.strokeStyle = sg; g.lineCap = 'round'; g.lineWidth = size * 0.02;
    g.beginPath(); g.moveTo(cx, cy); g.quadraticCurveTo(cx + size * 0.02, cy + R * 0.25, cx + size * 0.06, cy + R * 0.42); g.stroke();
    // body: light in the middle, deeper towards the tips
    path();
    var gr = g.createRadialGradient(cx, cy - R * 0.22, R * 0.04, cx, cy - R * 0.1, R * 1.05);
    gr.addColorStop(0, rgba(pal[0], 1)); gr.addColorStop(0.55, rgba(pal[0].map(function (v, n) { return Math.round(v * 0.6 + pal[1][n] * 0.4); }), 1)); gr.addColorStop(1, rgba(pal[1], 1));
    g.fillStyle = gr; g.fill();
    g.save(); path(); g.clip();
    for (var b = 0; b < 6; b++) {                                    // soft blotches of the neighbouring autumn colours
      var bx = cx + (rnd() - 0.5) * R * 1.4, by = cy - rnd() * R * 1.1, br = R * (0.14 + rnd() * 0.28), c = rnd() < 0.5 ? pal[1] : pal[3];
      var bg = g.createRadialGradient(bx, by, 0, bx, by, br); bg.addColorStop(0, rgba(c, 0.4)); bg.addColorStop(1, rgba(c, 0));
      g.fillStyle = bg; g.fillRect(bx - br, by - br, br * 2, br * 2);
    }
    for (var f = 0; f < 3 + rnd() * 3; f++) {                       // a few small age spots
      var fx = cx + (rnd() - 0.5) * R * 1.2, fy = cy - rnd() * R, fr = size * (0.006 + rnd() * 0.01);
      var fg = g.createRadialGradient(fx, fy, 0, fx, fy, fr); fg.addColorStop(0, 'rgba(70,30,12,0.4)'); fg.addColorStop(1, 'rgba(70,30,12,0)');
      g.fillStyle = fg; g.fillRect(fx - fr, fy - fr, fr * 2, fr * 2);
    }
    // veins: pale tapered midribs to every lobe tip, with fine side veins
    g.lineCap = 'round';
    tips.forEach(function (t) {
      for (var sgm = 0; sgm < 3; sgm++) {
        var q0 = sgm / 3, q1 = (sgm + 1) / 3;
        g.strokeStyle = 'rgba(255,228,180,' + (0.34 - sgm * 0.08) + ')'; g.lineWidth = size * (0.012 - sgm * 0.003);
        g.beginPath(); g.moveTo(cx + (t[0] - cx) * q0, cy + (t[1] - cy) * q0); g.lineTo(cx + (t[0] - cx) * q1, cy + (t[1] - cy) * q1); g.stroke();
      }
      g.strokeStyle = 'rgba(255,228,180,0.16)'; g.lineWidth = size * 0.005;
      for (var q = 0.3; q < 0.85; q += 0.18) {
        var mx = cx + (t[0] - cx) * q, my = cy + (t[1] - cy) * q, a = Math.atan2(t[1] - cy, t[0] - cx), l = R * 0.17 * (1 - q * 0.6);
        [-0.7, 0.7].forEach(function (o) { g.beginPath(); g.moveTo(mx, my); g.quadraticCurveTo(mx + Math.cos(a + o * 0.6) * l * 0.6, my + Math.sin(a + o * 0.6) * l * 0.6, mx + Math.cos(a + o) * l, my + Math.sin(a + o) * l); g.stroke(); });
      }
    });
    // light from the upper left, a little shade towards the lower right and round the edges
    var sh = g.createLinearGradient(cx - R, cy - R, cx + R, cy + R * 0.3); sh.addColorStop(0, 'rgba(255,240,210,0.18)'); sh.addColorStop(0.5, 'rgba(255,240,210,0)'); sh.addColorStop(1, 'rgba(40,10,0,0.2)');
    g.fillStyle = sh; g.fillRect(0, 0, size, size);
    var vg = g.createRadialGradient(cx, cy - R * 0.3, R * 0.45, cx, cy - R * 0.3, R * 1.15); vg.addColorStop(0, 'rgba(50,15,0,0)'); vg.addColorStop(1, 'rgba(50,15,0,0.28)');
    g.fillStyle = vg; g.fillRect(0, 0, size, size);
    g.restore();
    path(); g.strokeStyle = rgba(pal[1].map(function (v) { return Math.round(v * 0.55); }), 0.6); g.lineWidth = size * 0.007; g.stroke();
    return cv;
  }
  /* autumn palettes: [centre, edge, stem, blotch] */
  var MAPLE_PALS = [
    [[218, 64, 34], [146, 28, 20], [120, 50, 25], [238, 140, 42]],
    [[238, 110, 36], [178, 48, 22], [130, 60, 25], [214, 60, 32]],
    [[245, 156, 46], [200, 90, 28], [140, 75, 30], [214, 74, 30]],
    [[246, 198, 74], [220, 134, 40], [150, 95, 40], [236, 106, 36]],
    [[242, 172, 58], [192, 56, 26], [130, 60, 25], [246, 200, 82]],
    [[180, 108, 54], [116, 60, 30], [90, 50, 28], [204, 134, 62]],
    [[228, 86, 32], [152, 32, 20], [120, 50, 25], [246, 188, 70]],
    [[240, 132, 42], [164, 62, 24], [130, 60, 25], [184, 42, 26]]
  ];
  /* light a leaf photo for the candlelit room: warm light from the upper left, shade towards the lower right,
     a fold along the midrib (one half turned from the light), curled edges falling into shadow, and the whole
     leaf brought down to the room's dimmer, warmer exposure. Paints only on the leaf (source-atop). */
  function shadeLeaf(x, S) {
    x.setTransform(1, 0, 0, 1, 0, 0); x.filter = 'none'; x.globalCompositeOperation = 'source-atop';
    var lg = x.createLinearGradient(0, 0, S, S); lg.addColorStop(0, 'rgba(255,214,160,0.18)'); lg.addColorStop(0.42, 'rgba(255,214,160,0)'); lg.addColorStop(1, 'rgba(25,8,0,0.5)');
    x.fillStyle = lg; x.fillRect(0, 0, S, S);
    var fg = x.createLinearGradient(S * 0.3, 0, S * 0.7, 0); fg.addColorStop(0, 'rgba(20,6,0,0)'); fg.addColorStop(0.49, 'rgba(20,6,0,0)'); fg.addColorStop(0.53, 'rgba(20,6,0,0.16)'); fg.addColorStop(1, 'rgba(20,6,0,0.1)');
    x.fillStyle = fg; x.fillRect(0, 0, S, S);
    var rg = x.createRadialGradient(S * 0.44, S * 0.42, S * 0.14, S * 0.5, S * 0.5, S * 0.56); rg.addColorStop(0, 'rgba(30,10,0,0)'); rg.addColorStop(1, 'rgba(30,10,0,0.38)');
    x.fillStyle = rg; x.fillRect(0, 0, S, S);
    x.fillStyle = 'rgba(55,28,10,0.14)'; x.fillRect(0, 0, S, S);
    x.globalCompositeOperation = 'source-over';
  }
  /* real leaf photos (data-leaf-photos on the hero: the ones picked in the editor, or the theme's own leaf cut from the
     backdrop photo); each is fitted into a square sprite in four natural tints, mirrored on alternate copies.
     data-leaf-mix="1" keeps the generated maples in the mix alongside them. */
  function leafPhotos(done) {
    var el = document.querySelector('[data-leaf-photos]'), list = el ? el.getAttribute('data-leaf-photos').split('|').filter(Boolean) : [];
    if (!list.length) return;
    var mix = el.getAttribute('data-leaf-mix') === '1', out = [], left = list.length;
    var TINTS = ['none', 'hue-rotate(-8deg) saturate(1.1)', 'hue-rotate(6deg) brightness(1.04)', 'saturate(0.85) brightness(0.9)'];
    var finish = function () { if (--left === 0 && out.length) done(out, mix); };
    list.forEach(function (url) {
      var im = new Image(); im.crossOrigin = 'anonymous';
      im.onload = function () {
        TINTS.forEach(function (f, i) {
          var S = 160, c = document.createElement('canvas'); c.width = c.height = S; var x = c.getContext('2d');
          var k = S / Math.max(im.naturalWidth, im.naturalHeight), w = im.naturalWidth * k, h = im.naturalHeight * k;
          if (i % 2) { x.translate(S, 0); x.scale(-1, 1); }
          if (f !== 'none') x.filter = f;
          x.drawImage(im, (S - w) / 2, (S - h) / 2, w, h); shadeLeaf(x, S); out.push(c);
        });
        finish();
      };
      im.onerror = finish;
      im.src = url;
    });
  }
    var leaves = [], SPRITES = MAPLE_PALS.map(function (pal) { return mapleLeaf(80, pal); });
    leafPhotos(function (list, mix) { SPRITES = mix ? list.concat(SPRITES.slice(0, 4)) : list; leaves.forEach(function (l) { l.img = SPRITES[(Math.random() * SPRITES.length) | 0]; }); });
    var rnd = Math.random;
    function spawn(l, atTop) {
      var d = 0.35 + rnd() * 0.65;
      l.d = d; l.s = (phone ? 9 : 12) + d * (phone ? 9 : 16);
      l.x = rnd() * W; l.y = atTop ? -l.s * 2 : rnd() * H;
      l.a = rnd() * 6.283; l.va = (rnd() - 0.5) * 0.9; l.ph = rnd() * 6.283; l.fl = rnd() * 6.283;
      l.sp = 10 + d * 22; l.sw = 10 + d * 18; l.w1 = 0.45 + rnd() * 0.5; l.w2 = 0.6 + rnd() * 0.7;
      l.img = SPRITES[(rnd() * SPRITES.length) | 0]; l.al = 0.55 + d * 0.4;
    }
    function seed() { for (var i = 0; i < LEAVES; i++) { var l = {}; spawn(l, false); leaves.push(l); } }
    function drawLeaf(l, t) {
      var s = l.s, flip = Math.cos(t * l.w2 + l.fl);
      ctx.save();
      ctx.translate(l.x + Math.sin(t * l.w1 + l.ph) * l.sw, l.y);
      ctx.rotate(l.a);
      ctx.scale(Math.max(0.26, Math.abs(flip)), 1);
      ctx.globalAlpha = l.al * (0.7 + 0.3 * Math.abs(flip));
      ctx.drawImage(l.img, -s * 1.15, -s * 1.15, s * 2.3, s * 2.3);
      ctx.restore();
    }
    var lastT = 0;
    function clearLeaves() { if (ctx) ctx.clearRect(0, 0, W, H); }
    function stepLeaves(t, dt) {
      var wind = Math.sin(t * 0.09) * 6 + Math.sin(t * 0.023) * 4;
      ctx.clearRect(0, 0, W, H);
      leaves.forEach(function (l) {
        l.y += l.sp * dt;
        l.x += (wind * l.d + Math.cos(t * l.w1 + l.ph) * 4) * dt;
        l.a += l.va * dt;
        if (l.x < -l.s * 2) l.x = W + l.s; else if (l.x > W + l.s * 2) l.x = -l.s;
        if (l.y > H + l.s * 2) spawn(l, true); /* fully below the viewport: re-enter above it, invisible either way */
      });
      /* paint everything below the hero's bottom edge (the hero has its own leaves) */
      var top = Math.max(0, heroBot - sy);
      if (top >= H) return;
      ctx.save(); ctx.beginPath(); ctx.rect(0, top, W, H - top); ctx.clip();
      leaves.forEach(function (l) { if (l.y + l.s * 2 > top) drawLeaf(l, t); });
      ctx.restore();
    }

    /* ---- 3 · cursor glow ---- */
    var px = -1, py = -1, gx = -600, gy = -600, glowMoving = false, glowOn = false;
    function glowHit() {
      if (px < 0) { setGlow(false); return; }
      var y = py + sy, on = false, i, b;
      for (i = 0; i < bands.length; i++) {
        b = bands[i];
        if (y >= b.top && y < b.bottom) { on = b.dark && !b.el.classList.contains('rd-m-statement'); break; }
      }
      setGlow(on);
    }
    function setGlow(on) { if (on !== glowOn) { glowOn = on; glow.classList.toggle('is-on', on); } }
    function stepGlow() {
      var dx = px - gx, dy = py - gy;
      if (Math.abs(dx) + Math.abs(dy) < 0.4) { gx = px; gy = py; glowMoving = false; }
      else { gx += dx * 0.11; gy += dy * 0.11; }
      glow.style.transform = 'translate3d(' + (gx - 300).toFixed(1) + 'px,' + (gy - 300).toFixed(1) + 'px,0)';
    }
    function onMove(e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      px = e.clientX; py = e.clientY;
      if (gx < -500) { gx = px; gy = py; }
      glowMoving = true; glowHit(); kick();
    }
    function onOut(e) { if (!e.relatedTarget) { px = py = -1; setGlow(false); } }
    if (glow) {
      doc.addEventListener('pointermove', onMove, { passive: true });
      doc.addEventListener('pointerleave', onOut, { passive: true });
      doc.addEventListener('mouseout', onOut, { passive: true });
    }

    /* ---- frame loop: only while something is on screen and moving ---- */
    function frame(now) {
      if (disposed) return;
      var t = now / 1000, dt = lastT ? Math.min(0.05, t - lastT) : 0.016; lastT = t;
      var work = false;
      if (ctx && leafVisible > 0 && !doc.hidden) { stepLeaves(t, dt); work = true; }
      else if (ctx && doc.hidden) clearLeaves();
      if (glow && glowMoving) { stepGlow(); work = true; }
      if (work) raf(frame); else { running = false; lastT = 0; }
    }
    function kick() { if (!running && !disposed) { running = true; lastT = 0; raf(frame); } }

    /* ---- observers ---- */
    var hairIO = 'IntersectionObserver' in window && new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var hair = en.target.querySelector(':scope > .rd-m-ambient-hair');
        if (hair) {
          hair.classList.add('is-in', 'is-spark');
          setTimeout(function () { hair.classList.remove('is-spark'); }, 1500);
        }
        hairIO.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
    bands.forEach(function (b) { if (hairIO) hairIO.observe(b.el); else b.hair.classList.add('is-in'); });

    var leafIO = null; leafVisible = 1; leafOn = true; canvas.classList.add('is-on');

    var ro = 'ResizeObserver' in window && new ResizeObserver(queueMeasure);
    if (ro) { ro.observe(body); bands.forEach(function (b) { ro.observe(b.el); }); }

    function onVis() { if (doc.hidden) clearLeaves(); else kick(); }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', queueMeasure, { passive: true });
    window.addEventListener('load', queueMeasure);
    doc.addEventListener('visibilitychange', onVis);
    doc.addEventListener('shopify:section:load', queueMeasure);
    measure(); kick();

    /* if the user switches reduced-motion on mid-session, pack everything away */
    function dispose() {
      disposed = true;
      [bar, canvas, glow].forEach(function (e) { if (e && e.parentNode) e.parentNode.removeChild(e); });
      bands.forEach(function (b) { if (b.hair.parentNode) b.hair.parentNode.removeChild(b.hair); b.el.classList.remove('rd-m-ambient', 'rd-m-ambient--rel'); b.el.removeAttribute('data-rd-m-ambient'); });
      if (hairIO) hairIO.disconnect(); if (leafIO) leafIO.disconnect(); if (ro) ro.disconnect();
      window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', queueMeasure); window.removeEventListener('load', queueMeasure);
      doc.removeEventListener('visibilitychange', onVis); doc.removeEventListener('shopify:section:load', queueMeasure);
      doc.removeEventListener('pointermove', onMove); doc.removeEventListener('pointerleave', onOut); doc.removeEventListener('mouseout', onOut);
      html.removeAttribute('data-rd-m-ambient');
    }
    var onMq = function (e) { if (e.matches) { dispose(); if (mq.removeEventListener) mq.removeEventListener('change', onMq); } };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
